import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { init } from "ys-webrtc-sdk-core";
import api from "../../../axios/axiosInstance";

const initialState = {
  phone: null,
  pbx: null,
  destroy: null,
  username: null,
  signature: null,
  pbxURL: null,
  status: "disconnected",
  error: null,
  isFetchingSignature: false,
};

// Async thunk to fetch signature from backend
// Pass extensionNumber (string) to request a specific extension's signature
export const fetchYeastarSignature = createAsyncThunk(
  "yeastar/fetchSignature",
  async (extensionNumber = null, { rejectWithValue }) => {
    try {
      const body = extensionNumber ? { extensionNumber } : {};
      const response = await api.post("/api/yeastar-login/get-signature", body);

      if (response.data.status !== "success") {
        throw new Error(
          response.data.message || "Failed to get login signature"
        );
      }

      const { username, secret, pbxURL } = response.data.data;

      return { username, signature: secret, pbxURL };
    } catch (err) {
      console.error("❌ Failed to fetch signature:", err);
      return rejectWithValue(err.message || "Failed to fetch signature");
    }
  }
);

// Async thunk to switch the active extension (destroys current SDK, re-inits with new one)
// Pass { extensionNumber: "1020", destroyFn: destroyRef.current }
export const switchExtension = createAsyncThunk(
  "yeastar/switchExtension",
  async ({ extensionNumber, destroyFn }, { dispatch, rejectWithValue }) => {
    try {
      // Destroy the current SDK instance
      if (typeof destroyFn === "function") {
        try { destroyFn(); } catch (_) {}
      }

      // Clear Redux state so initializeYeastarSDK won't short-circuit
      dispatch(resetYeastar());

      // Fetch a new signature for the requested extension
      const creds = await dispatch(fetchYeastarSignature(extensionNumber)).unwrap();

      // Initialize SDK with the new extension's credentials
      const result = await dispatch(
        initializeYeastarSDK({
          username: creds.username,
          signature: creds.signature,
          pbxURL: creds.pbxURL,
        })
      ).unwrap();

      return result;
    } catch (err) {
      console.error("❌ Failed to switch extension:", err);
      return rejectWithValue(err.message || "Failed to switch extension");
    }
  }
);

// Async thunk to refresh credentials when token expires
export const refreshYeastarCredentials = createAsyncThunk(
  "yeastar/refreshCredentials",
  async (_, { dispatch, rejectWithValue }) => {
    try {

      // First, fetch new signature
      const signatureResult = await dispatch(fetchYeastarSignature()).unwrap();
      const { username, signature, pbxURL } = signatureResult;

      // Then reinitialize the SDK with new credentials
      const initResult = await dispatch(
        initializeYeastarSDK({ username, signature, pbxURL })
      ).unwrap();

      return initResult;
    } catch (err) {
      console.error("❌ Failed to refresh Yeastar credentials:", err);
      return rejectWithValue(err.message || "Failed to refresh credentials");
    }
  }
);

// Async thunk to initialize Yeastar SDK
export const initializeYeastarSDK = createAsyncThunk(
  "yeastar/initialize",
  async (
    { username, signature, pbxURL },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const { yeastar } = getState();

      // Check if already initialized
      if (yeastar.phone) {
        return {
          phone: yeastar.phone,
          pbx: yeastar.pbx,
          destroy: yeastar.destroy,
        };
      }


      const result = await init({
        username,
        secret: signature,
        pbxURL,
        enableLog: true,
      });

      const { phone, pbx, destroy } = result;
      phone.start();

      return { phone, pbx, destroy };
    } catch (err) {
      console.error("❌ Failed to initialize Yeastar SDK:", err);
      return rejectWithValue(err.message || "Failed to initialize SDK");
    }
  }
);

const yeastarSlice = createSlice({
  name: "yeastar",
  initialState,
  reducers: {
    setYeastarCredentials(state, action) {
      const { username, signature, pbxURL } = action.payload;
      state.username = username;
      state.signature = signature;
      state.pbxURL = pbxURL;
    },
    setYeastarSDK(state, action) {
      const { phone, pbx, destroy } = action.payload;
      state.phone = phone;
      state.pbx = pbx;
      state.destroy = destroy;
      state.status = "ready";
    },
    setYeastarStatus(state, action) {
      state.status = action.payload;
    },
    setYeastarError(state, action) {
      state.error = action.payload;
    },
    resetYeastar(state) {
      state.phone = null;
      state.pbx = null;
      state.destroy = null;
      state.username = null;
      state.signature = null;
      state.pbxURL = null;
      state.status = "disconnected";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch signature
      .addCase(fetchYeastarSignature.pending, (state) => {
        state.isFetchingSignature = true;
        state.error = null;
      })
      .addCase(fetchYeastarSignature.fulfilled, (state, action) => {
        state.isFetchingSignature = false;
        state.username = action.payload.username;
        state.signature = action.payload.signature;
        state.pbxURL = action.payload.pbxURL;
      })
      .addCase(fetchYeastarSignature.rejected, (state, action) => {
        state.isFetchingSignature = false;
        state.error = action.payload;
      })
      // Initialize SDK
      .addCase(initializeYeastarSDK.pending, (state) => {
        state.status = "connecting";
        state.error = null;
      })
      .addCase(initializeYeastarSDK.fulfilled, (state, action) => {
        state.phone = action.payload.phone;
        state.pbx = action.payload.pbx;
        state.destroy = action.payload.destroy;
        state.status = "ready";
        state.error = null;
      })
      .addCase(initializeYeastarSDK.rejected, (state, action) => {
        state.status = "disconnected";
        state.error = action.payload;
      })
      // Refresh credentials
      .addCase(refreshYeastarCredentials.pending, (state) => {
        state.status = "connecting";
        state.error = null;
      })
      .addCase(refreshYeastarCredentials.fulfilled, (state, action) => {
        state.phone = action.payload.phone;
        state.pbx = action.payload.pbx;
        state.destroy = action.payload.destroy;
        state.status = "ready";
        state.error = null;
      })
      .addCase(refreshYeastarCredentials.rejected, (state, action) => {
        state.status = "disconnected";
        state.error = action.payload;
      });
  },
});

export const {
  setYeastarCredentials,
  setYeastarSDK,
  setYeastarStatus,
  setYeastarError,
  resetYeastar,
} = yeastarSlice.actions;

export default yeastarSlice.reducer;

